"use client";

import { useMemo } from "react";
import { formatZec } from "@/lib/cypherpunk/format";
import WaffleGrid, { type WaffleCell } from "@/components/ui/WaffleGrid";

interface NetworkOwnershipWaffleProps {
  networkOwnershipPct: number;
  targetPct: number;
  circulatingSupply: number;
}

export default function NetworkOwnershipWaffle({
  networkOwnershipPct,
  targetPct,
  circulatingSupply,
}: NetworkOwnershipWaffleProps): React.ReactElement {
  const cells = useMemo<WaffleCell[]>(() => {
    const fullCells = Math.floor(networkOwnershipPct);
    const partialFrac = networkOwnershipPct - fullCells;
    const hasPartial = partialFrac > 0.001;
    const targetCells = Math.ceil(targetPct);
    const zecPerCell = Math.round(circulatingSupply / 100);

    return Array.from({ length: 100 }, (_, index) => {
      const label = `CELL ${index + 1}`;
      if (index < fullCells) {
        return {
          tone: "gold" as const,
          label: `${label} · OWNED`,
          value: `~${formatZec(zecPerCell)} ZEC`,
        };
      }

      if (hasPartial && index === fullCells) {
        const partialZec = Math.round(partialFrac * zecPerCell);
        return {
          tone: "partial" as const,
          label: `${label} · PARTIAL`,
          value: `${formatZec(partialZec)} ZEC (${Math.round(partialFrac * 100)}% of cell)`,
          partialHeight: Math.round(partialFrac * 100),
        };
      }

      if (index < targetCells) {
        return {
          tone: "target" as const,
          label: `${label} · TARGET`,
          value: `~${formatZec(zecPerCell)} ZEC to acquire`,
        };
      }

      return {
        tone: "rest" as const,
        label: `${label} · REST`,
        value: `~${formatZec(zecPerCell)} ZEC`,
      };
    });
  }, [networkOwnershipPct, targetPct, circulatingSupply]);

  return (
    <div className="waffle-panel">
      <WaffleGrid cells={cells} columns={10} />
    </div>
  );
}
